// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateStoreMutation } from "@/api/hooks/use-stores";
import { StorePlusIcon } from "@/components/icons";
import { DatePicker } from "@/components/shared/date-picker";
import { FormLabelRow } from "@/components/shared/form-label-row";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { STORE_DATE_FROM_YEAR, storeDateToMonth } from "@/features/stores/store-date-bounds";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { TIMEZONE_OPTIONS } from "@/lib/timezones";
import { type CreateStoreValues, createStoreSchema } from "./create-store-schema";

interface CreateStoreDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  duplicateFrom?: {
    copyFrom: string;
    chainName: string;
    timeZone: string;
  };
  /** Prefill the Chain field for a fresh create (e.g. when opened from a chain page). */
  defaultChainName?: string;
}

function toSnakeCase(str: string | undefined): string {
  if (!str) return "";
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

export function CreateStoreDialog({ open, onOpenChange, duplicateFrom, defaultChainName }: CreateStoreDialogProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { data: context } = useBackofficeContext();
  const createStore = useCreateStoreMutation();

  const [copyEnabled, setCopyEnabled] = useState(!!duplicateFrom);
  const [copyFrom, setCopyFrom] = useState(duplicateFrom?.copyFrom ?? "");
  const [storeNameManual, setStoreNameManual] = useState(false);

  const form = useForm<CreateStoreValues>({
    resolver: zodResolver(createStoreSchema),
    defaultValues: {
      storeName: "",
      chainName: duplicateFrom?.chainName ?? defaultChainName ?? "",
      storeCode: "",
      legibleName: "",
      timeZone: duplicateFrom?.timeZone ?? "",
      installDate: null,
    },
  });

  const watchedChain = form.watch("chainName");
  const watchedStoreCode = form.watch("storeCode");
  const chainSelected = !!watchedChain;

  // Auto-derive storeName from chain key + store code
  useEffect(
    function deriveStoreNameFromChainAndCode() {
      if (storeNameManual) return;
      const chain = toSnakeCase(watchedChain);
      const code = toSnakeCase(watchedStoreCode);
      form.setValue("storeName", chain && code ? `${chain}_${code}` : "");
    },
    [watchedChain, watchedStoreCode, storeNameManual, form],
  );

  useEffect(
    function resetFormOnOpen() {
      if (!open) return;
      setCopyEnabled(!!duplicateFrom);
      setCopyFrom(duplicateFrom?.copyFrom ?? "");
      setStoreNameManual(false);
      form.reset({
        storeName: "",
        legibleName: "",
        storeCode: "",
        chainName: duplicateFrom?.chainName ?? defaultChainName ?? "",
        timeZone: duplicateFrom?.timeZone ?? "",
        installDate: null,
      });
    },
    [open, duplicateFrom, defaultChainName, form],
  );

  async function onSubmit(values: CreateStoreValues) {
    try {
      // The dialog only collects basic info; the server seeds defaults for the remaining sections,
      // which can be fine-tuned afterwards on the store detail tabs.
      await createStore.mutateAsync({
        copyFrom: copyEnabled && copyFrom ? copyFrom : undefined,
        body: {
          storeName: values.storeName,
          chainName: values.chainName,
          timeZone: values.timeZone,
          info: {
            storeCode: values.storeCode,
            legibleName: values.legibleName,
            installDate: values.installDate ?? undefined,
            isActive: true,
          },
        },
      });
      toast.success(t("stores.notifications.created", { name: values.legibleName }));
      onOpenChange(false);
      form.reset();
      navigate({ to: "/stores/$storeName", params: { storeName: values.storeName } });
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        id="dialog-create-store"
        className="sm:max-w-xl p-0 gap-0 overflow-hidden flex flex-col"
        aria-describedby={undefined}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
            <div className="flex flex-row">
              <WizardPane icon={StorePlusIcon} />
              {/* Form fields pane */}
              <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
                <DialogHeader>
                  <DialogTitle>
                    {duplicateFrom ? t("stores.dialog.duplicateTitle") : t("stores.dialog.createTitle")}
                  </DialogTitle>
                </DialogHeader>

                <FormField
                  control={form.control}
                  name="chainName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow label={t("stores.form.chainName")} />
                      <FormControl>
                        <VirtualSelectPopover
                          value={field.value}
                          onChange={field.onChange}
                          options={(context?.chains ?? []).map((c) => ({ label: c.display, value: c.value }))}
                          placeholder={t("stores.form.selectChain")}
                          searchPlaceholder={t("stores.form.searchChains")}
                          emptyText={t("stores.form.noChains")}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <fieldset disabled={!chainSelected} className="flex flex-col gap-4 disabled:opacity-50">
                  <FormField
                    control={form.control}
                    name="legibleName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabelRow label={t("stores.form.legibleName")} />
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="storeCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabelRow label={t("stores.form.storeCode")} />
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="storeName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabelRow label={t("stores.form.storeName")} />
                        <FormControl>
                          <div className="relative">
                            <Input
                              {...field}
                              className={`font-mono ${!storeNameManual ? "border-dashed pr-16" : ""}`}
                              disabled={!storeNameManual}
                            />
                            {!storeNameManual && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 px-2 text-xs text-muted-foreground"
                                onClick={() => setStoreNameManual(true)}
                              >
                                <Pencil className="h-3 w-3 mr-1" />
                                Edit
                              </Button>
                            )}
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="timeZone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabelRow label={t("stores.form.timeZone")} />
                        <FormControl>
                          <VirtualSelectPopover
                            value={field.value}
                            onChange={field.onChange}
                            options={TIMEZONE_OPTIONS}
                            placeholder={t("stores.form.selectTimezone")}
                            searchPlaceholder={t("stores.form.searchTimezones")}
                            emptyText={t("stores.form.noTimezones")}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="installDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabelRow label={t("stores.form.installDate")} />
                        <FormControl>
                          <DatePicker
                            value={field.value ?? undefined}
                            onChange={(v) => field.onChange(v ?? null)}
                            placeholder={t("stores.form.notSet")}
                            fromYear={STORE_DATE_FROM_YEAR}
                            toMonth={storeDateToMonth()}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* Copy settings toggle */}
                  <div className="flex items-center gap-3">
                    <Switch id="copy-store-settings" checked={copyEnabled} onCheckedChange={setCopyEnabled} />
                    <Label htmlFor="copy-store-settings">{t("stores.form.copySettings")}</Label>
                  </div>

                  {copyEnabled && (
                    <VirtualSelectPopover
                      value={copyFrom}
                      onChange={setCopyFrom}
                      options={(context?.stores ?? []).map((s) => ({ label: s.display, value: s.value }))}
                      placeholder={t("stores.form.selectCopyFrom")}
                      searchPlaceholder={t("stores.form.searchCopyFrom")}
                      emptyText={t("stores.form.noCopyFrom")}
                    />
                  )}
                </fieldset>
              </div>
            </div>

            <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  form.reset();
                  onOpenChange(false);
                }}
              >
                {t("buttons.cancel")}
              </Button>
              <Button type="submit" disabled={createStore.isPending}>
                {createStore.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <StorePlusIcon className="h-4 w-4" />
                )}
                {t("buttons.create")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
